import { useState } from "react";
import {
  Input,
  Table,
  Typography,
  Tooltip,
  Row,
  Col,
  Tag,
  Divider,
  InputNumber,
  Switch,
} from "antd";
import { useQuery } from "@apollo/client";
import { GET_PRODUCTS } from "./requests";
import { productTypes } from "../../../../types";
import ProductList from "../../../../components/productsList";

const { Text } = Typography;

interface CouponDrawerProps {
  selectedProducts: {
    product: string;
    max: number;
    required: boolean;
  }[];
  setSelectedProducts: React.Dispatch<React.SetStateAction<{
    product: string;
    max: number;
    required: boolean;
  }[]>>;
}

const ExtrasForm = ({ selectedProducts, setSelectedProducts }: CouponDrawerProps) => {
  const [searchTitle, setSearchTitle] = useState("");
  const [pageConfig, setPageConfig] = useState({ page: 1,
    pageSize: 5 });

  const { data, loading: loadingProducts } = useQuery(GET_PRODUCTS, {
    variables: {
      params: pageConfig,
      name: searchTitle,
      type: "extra",
    },
  });

  const products = data?.products.results || [];

  const columns = [
    {
      title: "Nombre",
      key: "name",
      render: (row: typeof productTypes.product) => <Text strong>{row.name}</Text>,
    },
    {
      title: "Descripción",
      key: "description",
      render: (row: typeof productTypes.product) => <Text strong>{row.description}</Text>,
    },
    {
      title: "Variantes",
      key: "variants",
      render: (row: typeof productTypes.product) => {
        const lineItems =
          row.variants?.map((p) => ({
            product: {
              _id: p.id,
              img: p.img,
              title: p.title,
              description: p.description,
              product: {
                name: p.product?.name,
                description: p.product?.description ?? "",
              },
            },
          })) ?? [];

        return lineItems.length > 0 ? <ProductList lineItems={lineItems} /> : <Tag color="yellow">Sin Productos</Tag>;
      },
    },
    {
      title: "Maximo",
      key: "max",
      render: (row: typeof productTypes.productVariant) => {
        const selectedProduct = selectedProducts.find((item) => item.product === row.id);
        const max = selectedProduct ? selectedProduct.max : 0;

        return (
          <InputNumber
            min={0}
            defaultValue={max}
            onChange={(value) => handleQtyChange(row.id!, value ?? 0, "max")}
          />
        );
      },
    },
    {
      title: "Obligatorio",
      key: "required",
      render: (row: typeof productTypes.productVariant) => {
        const existingProduct = selectedProducts.find(item => item.product === row.id);

        return (
          <Switch
            checked={existingProduct?.required ?? false}
            onChange={(checked) => handleQtyChange(row.id!, checked, "required")}
          />
        );
      },
    },
  ];

  const handleSelect = (product: typeof productTypes.productVariant) => {
    setSelectedProducts((prevSelectedProducts) => {
      const existingProduct = prevSelectedProducts.find((item) => item.product === product.id);

      if (existingProduct) {
        return prevSelectedProducts;
      }

      return [...prevSelectedProducts, { product: product.id!,
        required: false,
        max: 0 }];
    });
  };

  const rowSelection = {
    selectedRowKeys: selectedProducts.map((product) => product.product),
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedProducts((prevSelectedProducts) => {
        return selectedRowKeys.map((key) => {
          const existingProduct = prevSelectedProducts.find((p) => p.product === key);
          return existingProduct || { product: key as string,
            required: false,
            max: 0 };
        });
      });
    },
    preserveSelectedRowKeys: true,
  };

  const handleQtyChange = (productId: string, value: number | boolean, type: "max" | "required") => {
    setSelectedProducts((prevSelectedProducts) => {
      return prevSelectedProducts.map((item) =>
        item.product === productId ? { ...item,
          [type]: value } : item);
    });
  };

  return (
    <>
      <Divider />
      <Row>
        <Col span={17}>
          <Tooltip title="Buscar Producto por Nombre">
            <Input
              placeholder="Buscar Producto por Nombre"
              value={searchTitle}
              onChange={(e) => setSearchTitle(e.target.value)}
              allowClear
            />
          </Tooltip>
        </Col>
        <Col span={7}>
          <Text style={{ marginLeft: 10,
            fontSize: "15px" }} strong>
            Productos Seleccionados: {selectedProducts.length}
          </Text>
        </Col>
      </Row>
      <div style={{ marginTop: 10 }}>
        <Table<typeof productTypes.productVariant>
          dataSource={products}
          rowKey="id"
          loading={loadingProducts}
          pagination={{
            current: Number(pageConfig.page),
            defaultCurrent: pageConfig.page,
            pageSize: Number(pageConfig.pageSize),
            defaultPageSize: pageConfig.pageSize,
            total: data?.products?.info?.count,
            showTotal: (total) => <Text disabled>{`${total} resultados`}</Text>,
            onChange: (page, pageSize) => {
              setPageConfig({ page,
                pageSize });
            },
            showSizeChanger: true,
          }}
          rowSelection={rowSelection}
          onRow={(record) => ({
            onClick: () => handleSelect(record),
          })}
          columns={columns}
        />
      </div>
    </>
  );
};

export default ExtrasForm;
